import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, CircleNotch, CheckCircle, Clock, Wrench, Package, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceTracker = () => {
  const [trackingId, setTrackingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const statusIcons = {
    Received: Clock,
    Diagnosed: MagnifyingGlass,
    "In Progress": Wrench,
    Completed: CheckCircle,
    Shipped: Truck,
  };

  const statusColors = {
    Received: "bg-amber-100 text-amber-800 border-amber-200",
    Diagnosed: "bg-blue-100 text-blue-800 border-blue-200",
    "In Progress": "bg-indigo-100 text-indigo-800 border-indigo-200",
    Completed: "bg-green-100 text-green-800 border-green-200",
    Shipped: "bg-purple-100 text-purple-800 border-purple-200",
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingId.trim()) {
      toast.error("Please enter a tracking ID");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await axios.get(
        `${API}/service-request/track/${trackingId.trim()}`
      );
      setResult(response.data);
      toast.success("Service request found!");
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        "Service request not found. Please check your tracking ID.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const StatusIcon = result ? statusIcons[result.status] || Clock : Clock;

  return (
    <section
      id="tracking"
      className="py-24 sm:py-32 bg-white"
      data-testid="tracking-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#0B2F6B]">
              Service Tracking
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-[#1A1A2E]">
              Track Your Repair
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-lg">
              Enter your tracking ID to get real-time updates on your laser repair status.
              We keep you informed every step of the way.
            </p>

            {/* Status Legend */}
            <div className="mt-8 space-y-3">
              <span className="text-sm font-semibold text-slate-900">
                Repair Stages:
              </span>
              <div className="flex flex-wrap gap-3">
                {Object.entries(statusIcons).map(([status, Icon]) => (
                  <div
                    key={status}
                    className="flex items-center gap-2 text-sm text-slate-600"
                  >
                    <Icon className="w-4 h-4" weight="duotone" />
                    <span>{status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Tracking Card */}
          <div className="bg-slate-50 border border-slate-200 p-8">
            <form onSubmit={handleTrack} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#1A1A2E] mb-2">
                  Tracking ID
                </label>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="e.g., LDR-A1B2C3D4"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                    className="flex-1 rounded-none border-slate-300 focus:border-[#0B2F6B] focus:ring-[#0B2F6B] uppercase"
                    data-testid="tracking-input"
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[#1E88FF] hover:bg-[#1976D2] text-white rounded-none px-6"
                    data-testid="track-button"
                  >
                    {loading ? (
                      <CircleNotch className="w-5 h-5 animate-spin" />
                    ) : (
                      <MagnifyingGlass className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </form>

            {/* Error State */}
            {error && (
              <div
                className="mt-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="tracking-error"
              >
                {error}
              </div>
            )}

            {/* Result */}
            {result && (
              <div className="mt-6 space-y-6" data-testid="tracking-result">
                {/* Status Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">
                      Current Status
                    </div>
                    <div
                      className={`inline-flex items-center gap-2 mt-1 px-3 py-1 text-sm font-semibold border ${
                        statusColors[result.status] || statusColors.Received
                      }`}
                    >
                      <StatusIcon className="w-4 h-4" weight="fill" />
                      {result.status}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">
                      Tracking ID
                    </div>
                    <div className="text-lg font-bold text-[#1A1A2E] font-mono">
                      {result.tracking_id}
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-slate-500">Customer</div>
                    <div className="font-medium text-[#1A1A2E]">{result.name}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Equipment</div>
                    <div className="font-medium text-[#1A1A2E]">
                      {result.laser_brand} {result.laser_model}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500">Submitted</div>
                    <div className="font-medium text-[#1A1A2E]">
                      {new Date(result.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500">Last Updated</div>
                    <div className="font-medium text-[#1A1A2E]">
                      {new Date(result.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                {result.status_history && result.status_history.length > 0 && (
                  <div className="border-t border-slate-200 pt-6">
                    <div className="text-sm font-semibold text-[#1A1A2E] mb-4">
                      Status History
                    </div>
                    <div className="space-y-3">
                      {result.status_history.map((item, index) => {
                        const ItemIcon = statusIcons[item.status] || Clock;
                        return (
                          <div
                            key={index}
                            className="flex items-start gap-3 text-sm"
                          >
                            <div className="w-8 h-8 bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <ItemIcon
                                className="w-4 h-4 text-slate-600"
                                weight="duotone"
                              />
                            </div>
                            <div>
                              <div className="font-medium text-[#1A1A2E]">
                                {item.status}
                              </div>
                              <div className="text-slate-500">{item.note}</div>
                              <div className="text-xs text-slate-400 mt-1">
                                {new Date(item.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceTracker;
